import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { isTerminalAuthError, SessionService } from 'src/app/core/session/session.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private readonly router = inject(Router);
    private readonly session = inject(SessionService);
    private readonly auth = inject(AuthService);

    /** Renewal in progress, shared by every request that got a 401 meanwhile. */
    private renewal$?: Observable<string>;

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                // Only requests that went out with the Auth0 token (AuthHttpInterceptor adds it, for our APIs only)
                // are retried: any other 401 is not about this session, and the token must not be sent to other hosts.
                if (error.status !== 401 || !req.headers.has('Authorization')) {
                    return throwError(() => error);
                }

                // Before assuming the session is invalid, ask the SDK for a token (it renews it with the refresh
                // token when the cached one has expired) and retry once. Only when the session itself cannot be
                // renewed is the user sent to sign in again. If the token is fine and the backend still rejects the
                // retry (permission not propagated, one specific endpoint…) it is not a session problem: the error
                // propagates and nobody is signed out. A failure to reach Auth0 (network) is not a dead session either.
                return this.renewToken().pipe(
                    catchError(() => throwError(() => error)),
                    switchMap((token) =>
                        next.handle(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))
                    )
                );
            })
        );
    }

    /**
     * One renewal for all the requests that fail together: concurrent refreshes with a rotating refresh token make
     * Auth0 treat the reuse as theft and revoke the whole session.
     */
    private renewToken(): Observable<string> {
        this.renewal$ ??= this.auth.getAccessTokenSilently().pipe(
            catchError((err) => {
                console.warn('[session] could not renew the access token after a 401:', err?.error ?? err?.message ?? err);
                if (!isTerminalAuthError(err)) {
                    return throwError(() => err);
                }
                // Session over: show the login page, which brings the user back to this very page afterwards.
                // Late 401s arrive when we are already there: navigating again would nest its URL as the destination.
                this.session.clear();
                if (!this.router.url.startsWith('/login')) {
                    void this.router.navigate(['/login'], { queryParams: { returnUrl: this.router.url } });
                }
                return throwError(() => err);
            }),
            finalize(() => (this.renewal$ = undefined)),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        return this.renewal$;
    }
}
